import {
  Table,
  Input,
  Select,
  Button,
  Typography,
  Skeleton,
  Avatar,
  Tooltip,
  Modal,
  Form,
  message,
} from "antd";
import {
  SearchOutlined,
  LeftOutlined,
  RightOutlined,
  EyeOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { useState, useCallback, useMemo, memo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "@/services/api";
import {
  ROLE_STYLES,
  USER_ROLES,
  USER_STATUSES,
  DEBOUNCE_DELAY,
} from "@/constants";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import {
  User,
  PaginatedResponse,
  UserRoleValue,
  UserStatusValue,
} from "@/types";

function debounce<T extends (...args: any[]) => void>(
  func: T,
  delay: number,
): T {
  let timeoutId: ReturnType<typeof setTimeout>;
  return ((...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  }) as T;
}

const RoleTag = memo(({ role }: { role: string }) => {
  const s = ROLE_STYLES[role] ?? { color: "#374151", bg: "#f3f4f6" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 700,
        color: s.color,
        background: s.bg,
        letterSpacing: "0.2px",
        whiteSpace: "nowrap",
      }}
    >
      {role}
    </span>
  );
});

const STATUS_DISPLAY: Record<string, { color: string; bg: string; label: string }> = {
  ACTIVE:           { color: "#065f46", bg: "#d1fae5", label: "ACTIVE" },
  SUSPENDED:        { color: "#991b1b", bg: "#fee2e2", label: "SUSPENDED" },
  INACTIVE:         { color: "#6b7280", bg: "#f3f4f6", label: "INACTIVE" },
  REJECTED:         { color: "#991b1b", bg: "#fee2e2", label: "REJECTED" },
  PENDING_APPROVAL: { color: "#92400e", bg: "#fef3c7", label: "PENDING" },
};

const StatusTag = memo(({ status, teacherStatus }: { status: UserStatusValue; teacherStatus?: string | null }) => {
  const effectiveKey =
    (teacherStatus === "REJECTED" || teacherStatus === "PENDING_APPROVAL" || teacherStatus === "SUSPENDED")
      ? teacherStatus
      : status;
  const s = STATUS_DISPLAY[effectiveKey] ?? STATUS_DISPLAY.INACTIVE;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 700,
        color: s.color,
        background: s.bg,
        whiteSpace: "nowrap",
      }}
    >
      {s.label}
    </span>
  );
});

function formatJoinedDate(value: string | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

interface EditUserFormValues {
  name: string;
  phone?: string;
  status: UserStatusValue;
}

export default function UsersPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [search, setSearch] = useState("");
  const [role, setRole] = useState<UserRoleValue | undefined>();
  const [status, setStatus] = useState<UserStatusValue | undefined>();
  const [cursor, setCursor] = useState<string | undefined>();
  const [cursorHistory, setCursorHistory] = useState<(string | undefined)[]>(
    [],
  );
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editForm] = Form.useForm<EditUserFormValues>();
  const page = cursorHistory.length + 1;

  const updateUserMutation = useMutation({
    mutationFn: (vars: { id: string; values: EditUserFormValues }) =>
      api.patch(`/admin/users/${vars.id}`, vars.values),
    onSuccess: (_resp, vars) => {
      message.success("User updated successfully");
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["user", vars.id] });
      qc.invalidateQueries({ queryKey: ["teachers"] });
      qc.invalidateQueries({ queryKey: ["teacher", vars.id] });
      qc.invalidateQueries({ queryKey: ["students"] });
      qc.invalidateQueries({ queryKey: ["student", vars.id] });
      setEditingUser(null);
      editForm.resetFields();
    },
    onError: (err: any) => {
      const msg =
        err?.error?.message ?? err?.message ?? "Failed to update user";
      message.error(msg);
    },
  });

  const openEdit = useCallback((user: User) => {
    setEditingUser(user);
  }, []);

  const closeEdit = useCallback(() => {
    setEditingUser(null);
    editForm.resetFields();
  }, [editForm]);

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearch(value);
        resetPagination();
      }, DEBOUNCE_DELAY),
    [],
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedSearch(e.target.value);
    },
    [debouncedSearch],
  );

  const { data, isLoading, isError } = useQuery({
    queryKey: ["users", search, role, status, cursor],
    queryFn: () =>
      api.get<PaginatedResponse<User>>("/admin/users", {
        params: {
          search: search || undefined,
          role,
          status: role === "TEACHER" ? undefined : status,
          teacherStatus: role === "TEACHER" ? status : undefined,
          cursor,
          limit: 20,
        },
      }),
  });

  const users: User[] = data?.data?.data ?? [];
  const meta = data?.data?.meta as { cursor?: string; hasMore?: boolean; total?: number } | undefined;
  const total = meta?.total ?? 0;

  if (isError) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 300,
          flexDirection: "column",
        }}
      >
        <Typography.Text style={{ color: "#ef4444", marginBottom: 16 }}>
          Failed to load users
        </Typography.Text>
        <Button onClick={() => window.location.reload()}>Retry</Button>
      </div>
    );
  }

  function handleNext() {
    setCursorHistory((h) => [...h, cursor]);
    setCursor(meta?.cursor);
  }
  function handlePrev() {
    const history = [...cursorHistory];
    const prev = history.pop();
    setCursorHistory(history);
    setCursor(prev);
  }
  function resetPagination() {
    setCursor(undefined);
    setCursorHistory([]);
  }
  const STATUS_OPTIONS_BY_ROLE: Record<string, { value: string; label: string }[]> = {
    STUDENT: [
      { value: "ACTIVE", label: "Active" },
      { value: "INACTIVE", label: "Inactive" },
      { value: "SUSPENDED", label: "Suspended" },
    ],
    TEACHER: [
      { value: "ACTIVE", label: "Active" },
      { value: "PENDING_APPROVAL", label: "Pending" },
      { value: "REJECTED", label: "Rejected" },
      { value: "SUSPENDED", label: "Suspended" },
    ],
    ADMIN: [
      { value: "ACTIVE", label: "Active" },
      { value: "INACTIVE", label: "Inactive" },
    ],
    SUPER_ADMIN: [
      { value: "ACTIVE", label: "Active" },
      { value: "INACTIVE", label: "Inactive" },
    ],
  };

  const statusOptions = role ? (STATUS_OPTIONS_BY_ROLE[role] ?? []) : [];

  function handleRole(value: UserRoleValue | undefined) {
    setRole(value);
    setStatus(undefined);
    resetPagination();
  }
  function handleStatus(value: UserStatusValue | undefined) {
    setStatus(value);
    resetPagination();
  }

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name: string) => (
        <span style={{ fontWeight: 600, color: "#0f172a" }}>{name}</span>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (v: string) => <span style={{ color: "#64748b" }}>{v}</span>,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      render: (v: string | undefined) => (
        <span style={{ color: "#64748b" }}>{v || "—"}</span>
      ),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (r: string) => <RoleTag role={r} />,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s: UserStatusValue, record: User) => (
        <StatusTag status={s} teacherStatus={record.teacherStatus} />
      ),
    },
    {
      title: "Joined",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (d: string | undefined) => (
        <span style={{ color: "#64748b" }}>{formatJoinedDate(d)}</span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 110,
      render: (_: unknown, record: User) => (
        <div style={{ display: "inline-flex", gap: 4 }}>
          <Tooltip title="View details">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              className="cw-action-icon"
              aria-label="View user"
              onClick={() => navigate(`/admin/users/${record.id}`)}
            />
          </Tooltip>
          <Tooltip title="Edit user">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              className="cw-action-icon cw-action-icon--edit"
              aria-label="Edit user"
              onClick={() => openEdit(record)}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <Typography.Title level={4} style={{ margin: 0, color: "#0f172a" }}>
          Users
        </Typography.Title>
        <Typography.Text style={{ color: "#64748b", fontSize: 13 }}>
          All registered users — teachers, students, and admins
        </Typography.Text>
      </div>

      <div
        style={{
          marginBottom: 16,
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <Input
          prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
          placeholder="Search by name…"
          onChange={handleSearchChange}
          style={{ flex: "1 1 180px", maxWidth: 260, height: 32 }}
          allowClear
        />
        <Select
          placeholder="Role"
          allowClear
          style={{ flex: "0 0 120px" }}
          onChange={handleRole}
        >
          {Object.values(USER_ROLES).map((r) => (
            <Select.Option key={r} value={r}>
              {r.charAt(0) + r.slice(1).toLowerCase()}
            </Select.Option>
          ))}
        </Select>
        <Tooltip title={!role ? "Select a role first" : undefined}>
          <Select
            placeholder="Status"
            allowClear
            disabled={!role}
            value={status}
            style={{ flex: "0 0 120px" }}
            onChange={handleStatus}
            options={statusOptions}
          />
        </Tooltip>
      </div>

      {isLoading ? (
        <div style={{ background: "#fff", borderRadius: 12, padding: 16 }}>
          <Skeleton active paragraph={{ rows: 5 }} />
        </div>
      ) : isMobile ? (
        /* ── Mobile card list ── */
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {users.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "40px 0",
                color: "#94a3b8",
              }}
            >
              No users found
            </div>
          ) : (
            users.map((user) => (
              <div
                key={user.id}
                style={{
                  background: "#fff",
                  borderRadius: 12,
                  border: "1px solid #e2e8f0",
                  padding: "14px 16px",
                }}
              >
                <div
                  onClick={() => navigate(`/admin/users/${user.id}`)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    marginBottom: 8,
                    cursor: "pointer",
                  }}
                >
                  <Avatar
                    size={38}
                    style={{
                      background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                      fontWeight: 700,
                      flexShrink: 0,
                      fontSize: 15,
                    }}
                  >
                    {user.name?.[0]?.toUpperCase()}
                  </Avatar>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        color: "#0f172a",
                        fontSize: 14,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {user.name}
                    </div>
                    <div
                      style={{
                        color: "#64748b",
                        fontSize: 12,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {user.email}
                    </div>
                    {user.phone && (
                      <div
                        style={{
                          color: "#94a3b8",
                          fontSize: 11,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {user.phone}
                      </div>
                    )}
                  </div>
                  <Tooltip title="Edit user">
                    <Button
                      type="text"
                      size="small"
                      icon={<EditOutlined />}
                      className="cw-action-icon cw-action-icon--edit"
                      aria-label="Edit user"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEdit(user);
                      }}
                    />
                  </Tooltip>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 6,
                  }}
                >
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <RoleTag role={user.role} />
                    <StatusTag status={user.status as UserStatusValue} teacherStatus={user.teacherStatus} />
                  </div>
                  <span style={{ color: "#94a3b8", fontSize: 12 }}>
                    {formatJoinedDate(user.createdAt)}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        /* ── Desktop table ── */
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          pagination={false}
          scroll={{ x: "max-content" }}
          style={{
            background: "#fff",
            borderRadius: 12,
            overflow: "hidden",
            border: "1px solid #e2e8f0",
          }}
          size="middle"
        />
      )}

      <div className="cw-pagination">
        <span className="cw-page-info">
          Page {page}{total > 0 && <> &middot; {total.toLocaleString()} user{total !== 1 ? 's' : ''}</>}
        </span>
        <Button
          icon={<LeftOutlined />}
          onClick={handlePrev}
          disabled={cursorHistory.length === 0}
        >
          Previous
        </Button>
        <Button
          icon={<RightOutlined />}
          onClick={handleNext}
          disabled={!meta?.hasMore}
        >
          Next
        </Button>
      </div>

      <Modal
        title="Edit User"
        open={!!editingUser}
        onCancel={closeEdit}
        onOk={() => editForm.submit()}
        okText="Save changes"
        confirmLoading={updateUserMutation.isPending}
        destroyOnHidden
        maskClosable={false}
      >
        <Form
          form={editForm}
          layout="vertical"
          preserve={false}
          initialValues={
            editingUser
              ? {
                  name: editingUser.name,
                  email: editingUser.email,
                  phone: editingUser.phone ?? "",
                  status: editingUser.status,
                }
              : undefined
          }
          onFinish={(values) => {
            if (!editingUser) return;
            const payload: EditUserFormValues = {
              name: values.name,
              phone: values.phone?.trim() ? values.phone.trim() : undefined,
              status: values.status,
            };
            updateUserMutation.mutate({ id: editingUser.id, values: payload });
          }}
          style={{ marginTop: 8 }}
        >
          <Form.Item
            name="name"
            label="Full Name"
            rules={[
              { required: true, message: "Name is required" },
              { min: 2, max: 100 },
            ]}
          >
            <Input placeholder="Enter full name" />
          </Form.Item>
          <Form.Item name="email" label="Email">
            <Input disabled style={{ color: "#64748b", background: "#f8fafc", cursor: "not-allowed" }} />
          </Form.Item>
          <Form.Item
            name="phone"
            label="Phone"
            rules={[
              {
                pattern: /^[+]?[0-9\s\-()]{7,20}$/,
                message: "Invalid phone number format",
              },
            ]}
          >
            <Input placeholder="+91 9876543210" />
          </Form.Item>
          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: "Status is required" }]}
          >
            <Select
              options={Object.values(USER_STATUSES).map((s) => ({
                value: s,
                label: s.charAt(0) + s.slice(1).toLowerCase(),
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
